import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CircleNotch, Timer, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public English Assessment.
 * No portal branding in the URL: `/english-assessment`.
 * Flow: intake form → timed quiz (Reading MCQ → Writing MCQ → WritePlacer essay) → thank-you page.
 */
export default function EnglishAssessment() {
  const [phase, setPhase] = useState('intake'); // intake | quiz | done
  const [intake, setIntake] = useState({ first_name: '', last_name: '', dob: '', email: '', phone: '' });
  const [starting, setStarting] = useState(false);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({}); // { question_id: option_id }
  const [essay, setEssay] = useState('');
  const [section, setSection] = useState(0); // 0=reading, 1=writing, 2=essay
  const [submitting, setSubmitting] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [finalScores, setFinalScores] = useState(null);

  const readingQs = session?.questions?.reading || [];
  const writingQs = session?.questions?.writing || [];
  const essayPrompt = session?.questions?.writeplacer_prompt || '';

  // Countdown timer for the whole test.
  useEffect(() => {
    if (phase !== 'quiz' || !session) return;
    const iv = setInterval(() => setRemaining(r => Math.max(0, r - 1)), 1000);
    return () => clearInterval(iv);
  }, [phase, session]);

  useEffect(() => {
    if (phase === 'quiz' && remaining === 0 && session) {
      handleSubmit(true); // auto-submit on timeout
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, phase]);

  const mmss = useMemo(() => {
    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }, [remaining]);

  const handleStart = async () => {
    const fields = ['first_name', 'last_name', 'dob', 'email', 'phone'];
    for (const f of fields) {
      if (!String(intake[f] || '').trim()) { toast.error('Please fill every field'); return; }
    }
    setStarting(true);
    try {
      const res = await axios.post(`${API}/english-assessment/start`, intake);
      setSession(res.data);
      setRemaining(res.data.time_limit_seconds || 45 * 60);
      setPhase('quiz');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start the test');
    } finally { setStarting(false); }
  };

  const handleSubmit = async (autoTriggered = false) => {
    if (submitting || !session) return;
    setSubmitting(true);
    try {
      const payload = {
        answers: Object.entries(answers).map(([question_id, selected_option_id]) => ({ question_id, selected_option_id })),
        essay,
      };
      const res = await axios.post(`${API}/english-assessment/${session.session_id}/submit`, payload);
      setFinalScores(res.data.section_scores);
      setPhase('done');
      if (autoTriggered) toast.info('Time is up — your test has been submitted automatically.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit');
    } finally { setSubmitting(false); }
  };

  // ------ Intake phase ------
  if (phase === 'intake') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-10" data-testid="assessment-intake">
        <div className="w-full max-w-lg bg-white rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Libre Baskerville', serif" }}>English Placement Assessment</h1>
          <p className="text-sm text-gray-500 mb-6">Please fill in your details. The test takes about 45 minutes and covers Reading, Writing, and a short essay.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">First Name *</Label><Input value={intake.first_name} onChange={e => setIntake(p => ({ ...p, first_name: e.target.value }))} data-testid="intake-first" /></div>
            <div><Label className="text-xs">Last Name *</Label><Input value={intake.last_name} onChange={e => setIntake(p => ({ ...p, last_name: e.target.value }))} data-testid="intake-last" /></div>
            <div><Label className="text-xs">Date of Birth *</Label><Input type="date" value={intake.dob} onChange={e => setIntake(p => ({ ...p, dob: e.target.value }))} data-testid="intake-dob" /></div>
            <div><Label className="text-xs">Phone *</Label><Input value={intake.phone} onChange={e => setIntake(p => ({ ...p, phone: e.target.value }))} placeholder="+1 604 555 0100" data-testid="intake-phone" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Email *</Label><Input type="email" value={intake.email} onChange={e => setIntake(p => ({ ...p, email: e.target.value }))} data-testid="intake-email" /></div>
          </div>
          <Button onClick={handleStart} disabled={starting} className="w-full mt-6 bg-blue-700 hover:bg-blue-800 text-white h-11" data-testid="intake-start">
            {starting ? <><CircleNotch size={16} className="mr-2 animate-spin" /> Starting…</> : <>Start Test <ArrowRight size={16} className="ml-2" /></>}
          </Button>
          <p className="text-[11px] text-gray-400 mt-3 text-center">Once you start, the 45-minute timer runs continuously. Please complete in one sitting.</p>
        </div>
      </div>
    );
  }

  // ------ Done phase ------
  if (phase === 'done') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center px-4 py-10" data-testid="assessment-done">
        <div className="w-full max-w-lg bg-white rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 mx-auto mb-4 flex items-center justify-center">
            <CheckCircle size={30} weight="fill" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>Thank you!</h1>
          <p className="text-sm text-gray-600 mb-4">Your test has been submitted. Our team will review your essay and get back to you within 1–2 business days.</p>
          {finalScores && (
            <div className="border border-gray-200 rounded-lg p-4 mt-4 bg-slate-50 grid grid-cols-2 gap-3 text-left">
              <div><p className="text-xs text-gray-500">Reading</p><p className="text-lg font-semibold text-gray-900">{finalScores.reading.score} / {finalScores.reading.total}</p></div>
              <div><p className="text-xs text-gray-500">Writing</p><p className="text-lg font-semibold text-gray-900">{finalScores.writing.score} / {finalScores.writing.total}</p></div>
              <div className="col-span-2 text-[11px] text-gray-500 pt-1">Essay is graded manually by our team.</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ------ Quiz phase ------
  const renderMcq = (q, idx) => (
    <div key={q.id} className="border border-gray-200 bg-white rounded-lg p-4" data-testid={`q-${q.id}`}>
      {q.prompt && <p className="text-sm text-gray-600 italic mb-3 whitespace-pre-line">{q.prompt}</p>}
      <p className="text-sm font-semibold text-gray-900 mb-3">{idx + 1}. {q.question}</p>
      <div className="space-y-2">
        {(q.options || []).map(opt => (
          <label key={opt.id} className={`flex items-start gap-3 cursor-pointer rounded-md border p-2.5 transition-colors ${answers[q.id] === opt.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
            <input type="radio" name={q.id} className="mt-1" checked={answers[q.id] === opt.id} onChange={() => setAnswers(a => ({ ...a, [q.id]: opt.id }))} data-testid={`q-${q.id}-opt-${opt.id}`} />
            <span className="text-sm text-gray-800">{opt.text}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const sections = [
    { label: 'Section 1 · Reading', questions: readingQs },
    { label: 'Section 2 · Writing', questions: writingQs },
    { label: 'Section 3 · WritePlacer (Essay)', questions: [] },
  ];
  const current = sections[section];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="assessment-quiz">
      {/* Sticky header with timer */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500">English Placement Assessment</p>
            <p className="text-sm font-semibold text-gray-900">{current.label}</p>
          </div>
          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${remaining < 300 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`} data-testid="assessment-timer">
            <Timer size={16} /> {mmss}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {section < 2 ? (
          current.questions.map((q, idx) => renderMcq(q, idx))
        ) : (
          <div className="border border-gray-200 bg-white rounded-lg p-4">
            <p className="text-sm font-semibold text-gray-900 mb-2">WritePlacer — Essay</p>
            <p className="text-sm text-gray-600 whitespace-pre-line mb-4">{essayPrompt}</p>
            <textarea
              className="w-full min-h-[320px] p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Write your essay here…"
              value={essay}
              onChange={e => setEssay(e.target.value)}
              data-testid="assessment-essay"
            />
            <p className="text-[11px] text-gray-500 mt-2">Word count: {essay.trim().split(/\s+/).filter(Boolean).length}</p>
          </div>
        )}

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => setSection(s => Math.max(0, s - 1))} disabled={section === 0}>← Previous</Button>
          {section < 2 ? (
            <Button className="bg-blue-700 hover:bg-blue-800 text-white" onClick={() => { window.scrollTo(0, 0); setSection(s => s + 1); }}>Next Section →</Button>
          ) : (
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => handleSubmit(false)} disabled={submitting} data-testid="assessment-submit">
              {submitting ? <><CircleNotch size={14} className="mr-2 animate-spin" /> Submitting…</> : 'Submit Test'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
